import React, { useEffect } from 'react';
import { Select, Spin, Tag } from 'antd';
import debounce from 'lodash/debounce';
import { api, omit } from '@unicus/react-ui-kit';
import { CloseOutlined } from '@ant-design/icons';

const { Option } = Select;

const initialState = {
  data: [],
  cache: [],
  value: [],
  textSearched: '',
  fetching: false,
  open: false,
  pagination: {
    hasNextPage: true,
    currentPage: 0
  },
  loaded: false
};

class RemoteSelect extends React.Component {
  constructor(props) {
    super(props);
    this.lastFetchId = 0;
    this.handleSearch = debounce(this.fetchData, 800);
    this.fethOnMount = props.fethOnMount;
    this.state = { ...initialState };
    this.remoteSelect = React.createRef();
  }

  componentDidMount() {
    if (this.fethOnMount) {
      this.fetchData();
    }
  }

  shouldComponentUpdate(nextProps) {
    const { params } = this.props;
    const { params: nextParams } = nextProps;

    if (
      params !== nextParams &&
      JSON.stringify(params) !== JSON.stringify(nextParams)
    ) {
      this.lastFetchId = 0;
      this.setState((prevState) => ({
        ...prevState,
        ...initialState
      }));
    }

    return true;
  }

  fetchData = (value) => {
    const { pagination, loaded } = this.state;
    const {
      resource,
      params = {},
      sorting,
      defaultSorting = true,
      autoFocus,
      tenant,
      onFetching,
      onFetched,
      normalizeFromSelectLabel,
      defaultActiveFirstOption
    } = this.props;
    if (!pagination.hasNextPage) {
      return;
    }

    onFetching?.(value);

    this.setState({ fetching: true });
    this.lastFetchId += 1;
    const fetchId = this.lastFetchId;
    const queryParams = {
      filterText: value,
      page: pagination.currentPage + 1,
      sorting: sorting || (defaultSorting ? 'Name' : null),
      ...params
    };
    api.fetch(resource, queryParams, null, tenant).then((response = {}) => {
      const { data = { result: {} } } = response;
      if (fetchId !== this.lastFetchId) {
        return;
      }
      onFetched?.(data.result);
      const { totalCount = 0, items = [] } = data.result;
      const newItems = items.map((unit) => ({
        key: `${unit.id}`,
        label: normalizeFromSelectLabel
          ? normalizeFromSelectLabel(unit)
          : unit.name ?? unit.title,
        item: unit
      }));

      if (
        !loaded &&
        defaultActiveFirstOption &&
        newItems.length === 1 &&
        !this.props.value
      ) {
        const { onChange } = this.props;

        if (onChange) {
          onChange(newItems[0]);
        }
      }

      this.setState((prevState) => {
        const data =
          queryParams.page === 1 ? newItems : [...prevState.data, ...newItems];

        const cache = [...prevState.cache, ...data].filter(
          (value, index, self) =>
            self.map((c) => c.item.id).indexOf(value.item.id) === index
        );

        return {
          ...prevState,
          data,
          cache,
          fetching: false,
          open: this.lastFetchId === 1 ? autoFocus : prevState.open,
          textSearched: value,
          pagination: {
            hasNextPage:
              queryParams.page === 1
                ? newItems.length < totalCount
                : prevState.data.length + newItems.length < totalCount,
            currentPage: queryParams.page
          },
          loaded: true
        };
      });
    });
  };

  filterObjectByKey = (value, key) => {
    return key === String(value.id);
  };

  getObject = (key) => {
    const { cache } = this.state;
    const result = cache.filter((c) => this.filterObjectByKey(c.item, key));
    if (result.length === 1) {
      return result[0];
    }
    return null;
  };

  handleSelect = (object = {}) => {
    const { key } = object;
    const { onSelect } = this.props;

    if (!onSelect || !key) {
      return;
    }

    const objectSelected = this.getObject(key);
    onSelect(objectSelected.item);
  };

  onDeselect = (object = {}) => {
    const { key } = object;
    const { onDeselect } = this.props;
    if (!onDeselect || !key) {
      return;
    }
    const objectSelected = this.getObject(key);
    onDeselect(objectSelected?.item);
  };

  triggerChange = (object) => {
    const { onDeselect, onChange } = this.props;

    if (object) {
      this.handleSelect(object);
    } else if (onDeselect && !object) {
      onDeselect();
    }

    if (onChange) {
      if (Array.isArray(object)) {
        onChange(object.map((c) => this.getObject(c?.key)));
        return;
      }

      onChange(this.getObject(object?.key));
    }
  };

  handleOnSearch = (value) => {
    const { onSearch } = this.props;
    this.setState({ text: value });

    onSearch?.(value);
    this.setState({ pagination: { hasNextPage: true, currentPage: 0 } });

    if (this.state.open) {
      this.handleSearch(value);
    }
  };

  handleClear = () => {
    this.handleOnSearch(null);
  };

  handleDropdownVisibleChange = (visible) => {
    const { onDropdownVisibleChange } = this.props;

    if (visible && this.lastFetchId === 0) {
      this.fetchData();
      return;
    }

    this.setState({ open: visible });
    onDropdownVisibleChange?.(visible);
  };

  handleScroll = (event) => {
    const { textSearched } = this.state;
    const { target } = event;
    if (
      Math.ceil(target.scrollTop + target.offsetHeight) >= target.scrollHeight
    ) {
      this.fetchData(textSearched);
    }
  };

  blur() {
    this.remoteSelect.current.blur();
  }

  focus() {
    this.remoteSelect.current.focus();
  }

  reset() {
    this.lastFetchId = 0;
    this.setState((prevState) => ({
      ...prevState,
      ...initialState,
      cache: prevState.cache
    }));
  }

  add(item) {
    this.setState((prevState) => ({
      ...prevState,
      data: [...prevState.data, { key: item.id, label: item.name, item }],
      cache: [...prevState.cache, { key: item.id, label: item.name, item }]
    }));
  }

  getByKey(key) {
    return this.state.cache.find((c) => c.key === key)?.item;
  }

  addCaches(items) {
    this.setState((prevState) => ({
      ...prevState,
      cache: [
        ...prevState.cache,
        ...items.map((c) => ({ key: c.id, label: c.name, item: c }))
      ]
    }));
  }

  isEmpty() {
    return !this.state.fetching && this.state.data.length === 0;
  }

  triggerTagRender = (props) => {
    const { label, value, onClose } = props;

    if (this.props.tagRender) {
      const objectSelected = this.getObject(value);
      return this.props.tagRender(props, objectSelected?.item);
    }

    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <div className="ant-select-selection-overflow-item">
        <span className="ant-select-selection-item">
          <span className="ant-select-selection-item-content">{label}</span>
          <span className="ant-select-selection-item-remove">
            <CloseOutlined onMouseDown={onPreventMouseDown} onClick={onClose} />
          </span>
        </span>
      </div>
    );
  };

  handleMaxTagPlaceholder = (values) => {
    const { maxTagPlaceholder } = this.props;
    return maxTagPlaceholder(values);
  };

  render() {
    const { fetching, data, open } = this.state;
    const {
      optionRender,
      placeholder,
      style,
      notFoundContent,
      customDropdownRender,
      maxTagPlaceholder,
      showSearch = true,
      filterOption = false,
      allowClear = true
    } = this.props;

    return (
      <>
        <Select
          {...omit(this.props, [
            'customDropdownRender',
            'notFoundContent',
            'onFetched',
            'onFetching',
            'fethOnMount',
            'onChange',
            'onSelect'
          ])}
          showSearch={showSearch}
          labelInValue
          allowClear={allowClear}
          onChange={this.triggerChange}
          tagRender={this.triggerTagRender}
          ref={this.remoteSelect}
          onClear={this.handleClear}
          open={open}
          maxTagPlaceholder={
            maxTagPlaceholder ? this.handleMaxTagPlaceholder : undefined
          }
          onDropdownVisibleChange={this.handleDropdownVisibleChange}
          onPopupScroll={this.handleScroll}
          filterOption={filterOption}
          notFoundContent={
            fetching ? null : notFoundContent && notFoundContent()
          }
          placeholder={placeholder}
          dropdownRender={(menu) =>
            customDropdownRender ? (
              customDropdownRender({ menu, fetching })
            ) : (
              <>
                {menu}
                {fetching && (
                  <div className="gx-remote-select-spin">
                    <Spin size="default" />
                  </div>
                )}
              </>
            )
          }
          onSearch={this.handleOnSearch}
          onDeselect={(item) => this.onDeselect(item)}
          style={style || { width: '100%' }}
        >
          {data.map((d) =>
            optionRender ? (
              optionRender(d)
            ) : (
              <Option key={d.key} value={d.key}>
                {d.label}
              </Option>
            )
          )}
        </Select>
      </>
    );
  }
}
RemoteSelect.defaultProps = { fethOnMount: true };
export default RemoteSelect;
